import React, { cloneElement, useContext, useState } from "react";
import { DocumentPart } from "../../api/models";
import { Breadcrumb, Icon, mergeStyleSets, TooltipHost } from "@fluentui/react";
import { formatMessage } from "../../utils/helpers/LanguageHelper";
import { fetchDocumentRoots } from "../../api/document/Location";
import { FileLocation, FileLocationProviderContext } from "../document/FileLocationProvider";
import {
  CheckboxVisibility,
  DetailsList,
  DetailsListLayoutMode,
  IColumn,
  SelectionMode,
} from "@fluentui/react/lib/DetailsList";
import { VirtualTree } from "./virtual-tree/VirtualTree";
import { DocumentRoot } from "../../api/models/document-root";
import { mergeStyles } from "@fluentui/react/lib/Styling";
import { generateIdentifier } from "../../utils/helpers/RobotFramework";

const classNames = mergeStyleSets({
  fileIconHeaderIcon: {
    padding: 0,
    fontSize: "16px",
  },
  fileIconCell: {
    textAlign: "center",
    selectors: {
      "&:before": {
        content: ".",
        display: "inline-block",
        verticalAlign: "middle",
        height: "100%",
        width: "0px",
        visibility: "hidden",
      },
    },
  },
  pageContainer: {
    position: "relative",
  },
  contentWrap: {
    overflow: "auto",
  },
  content: {
    position: "relative",
  },
  contentHeader: {
    maxHeight: "25vh",
  },
});

export interface FlatPathTreeItem {
  key: string;
  text: string;
  iconName?: string;
  path?: string;
  document?: DocumentPart;
  readPermission?: boolean;
}

export interface FlatPathTreeItemExplored {
  item: FlatPathTreeItem;
  path: FlatPathTreeItem[];
  children: FlatPathTreeItem[];
}

export interface FlatPathTreeExplorerProps {
  item: FlatPathTreeItemExplored;
  onItemClicked: (item: FlatPathTreeItem) => void;
  onItemSelection: (item: FlatPathTreeItem) => void;
  page?: string;
  selectedItem?: FlatPathTreeItem;
  searchQuery?: string;

  showDownloadButton?: boolean;
}

/**
 * General components to explore a tree structure in a flat view
 * with a top path tracer
 * @param props
 * @constructor
 */
const columns: IColumn[] = [
  {
    key: "column1",
    name: "File Type",
    className: classNames.fileIconCell,
    iconClassName: classNames.fileIconHeaderIcon,
    ariaLabel: "Column operations for File type, Press to sort on File type",
    iconName: "Page",
    isIconOnly: true,
    fieldName: "name",
    minWidth: 16,
    maxWidth: 16,
    onRender: (item: FlatPathTreeItem) => (
      <TooltipHost>
        <Icon className={classNames.fileIconHeaderIcon} iconName={item.iconName ?? "FabricFolder"}></Icon>
      </TooltipHost>
    ),
  },
  {
    key: "column2",
    name: "Name",
    fieldName: "text",
    minWidth: 210,
    maxWidth: 500,
    isRowHeader: true,
    isResizable: true,
    data: "string",
    isPadded: true,
    onRender: (item: FlatPathTreeItem) => <span style={{ cursor: "pointer" }}>{item.text}</span>,
  },
];

const detailsListCustomStyles = {
  root: mergeStyles({ overflowX: "hidden" }),
};

export const FlatPathTreeExplorer: React.FunctionComponent<FlatPathTreeExplorerProps> = (props) => {
  const { item, onItemClicked, onItemSelection, page, searchQuery } = props;
  const [children, setChildren] = useState(item.children);
  const [firstNewItemValue, setFirstNewItemValue] = useState("");
  const fileLocationProvider = useContext(FileLocationProviderContext);

  function onClick(clickedItem: FlatPathTreeItem) {
    if (clickedItem.key != item.item.key) onItemClicked(clickedItem);
  }

  function loadDocumentPart(documentRoot: DocumentRoot) {
    // eslint-disable-next-line no-undef
    console.log("CLICK ITEM", documentRoot);
    //setChildren([{ key: "test", text: "TEST" }]);

    const clickedItem: FlatPathTreeItem = {
      key: documentRoot.documentPart.driveItemId,
      text: documentRoot.documentPart.name,
      document: documentRoot.documentPart,
      readPermission: documentRoot.hasReadPermissions,
    };
    onItemSelection(clickedItem);
    return documentRoot;
  }

  function openDocument(documentPart: DocumentPart) {
    const clickedItem: FlatPathTreeItem = {
      key: documentPart.driveItemId,
      text: documentPart.name,
      document: documentPart,
    };
    onItemClicked(clickedItem);
    return documentPart;
  }

  async function virtualScrollAddItems(searchQuery, page) {
    const embeddedRoots = await fetchDocumentRoots(searchQuery, page);
    let fileLocations = embeddedRoots._embedded.content.map<FileLocation>((df) => {
      return {
        key: df.documentPart.driveItemId,
        text: df.documentPart.name.replace(/\.docx$/, ""),
        iconName: "WordLogoInverse",
        document: df.documentPart,
        readPermission: df.hasReadPermissions,
      };
    });
    setChildren((children) => [...children, ...fileLocations]);
    fileLocationProvider.updateChildren([...children, ...fileLocations], embeddedRoots.page);
    setFirstNewItemValue(fileLocations[0].key);
  }

  return (
    <div>
      {page !== "OPEN" && (
        <div className={classNames.contentHeader}>
          <Breadcrumb
            items={[...item.path, item.item].map((iitem) => {
              return {
                ...iitem,
                onClick: (_, clickedItem) => onClick(clickedItem),
              };
            })}
            maxDisplayedItems={10}
          />
        </div>
      )}
      <div className={classNames.pageContainer}>
        <div className={classNames.contentWrap}>
          <div className={classNames.content}>
            {page === "OPEN" && (
              <VirtualTree
                childrenItems={children}
                loadDocumentPart={(item) => loadDocumentPart(item)}
                openDocument={(item) => openDocument(item)}
                virtualScrollAddItems={(page) => virtualScrollAddItems(searchQuery, page)}
                pagination={fileLocationProvider.exploredLocation.page}
                firstNewItemValue={firstNewItemValue}
                selectedItem={props.selectedItem}
                showDownloadButton={props.showDownloadButton}
              />
            )}
            {page !== "OPEN" && (
              <DetailsList
                styles={detailsListCustomStyles}
                onShouldVirtualize={() => false}
                items={item.children}
                compact={true}
                selectionMode={SelectionMode.single}
                columns={columns}
                getKey={(i) => i.key}
                checkboxVisibility={CheckboxVisibility.hidden}
                onRenderRow={(row, defaultRender) => {
                  return cloneElement(defaultRender(row), {
                    id: generateIdentifier(row.item.text, "row"),
                    onDoubleClick: () => onItemClicked(row.item),
                    onClick: () => onItemSelection(row.item),
                  });
                }}
                selectionPreservedOnEmptyClick
                layoutMode={DetailsListLayoutMode.justified}
              />
            )}
            {page === "EXPORT" && (
              <div
                style={{
                  backgroundColor: "var(--color-yellow-a20)",
                  fontSize: "1rem",
                  margin: "1.25rem 1.25rem 1.25rem 1.25rem",
                  padding: "0.5rem 1.25rem 0.5rem 1.25rem",
                }}
              >
                <b style={{ color: "var(--color-yellow)" }}>{formatMessage("cof.file.open.export.title.attention")}</b>
                <br />
                {formatMessage("cof.file.open.export.message.aligntargetenvironment")}
              </div>
            )}
            {page === "IMPORT" && (
              <div
                style={{
                  backgroundColor: "var(--color-yellow-a20)",
                  fontSize: "1rem",
                  margin: "1.25rem 1.25rem 1.25rem 1.25rem",
                  padding: "0.5rem 1.25rem 0.5rem 1.25rem",
                }}
              >
                <b style={{ color: "var(--color-yellow)" }}>{formatMessage("cof.file.open.export.title.attention")}</b>
                <br />
                {formatMessage("cof.import.message.aligntargetenvironment")}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
